import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    Button,
    Badge,
    CardHeader
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, apiGet, uploadFile, mediaUrl } from "../../Utils";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Plus, Trash2, Disc, Tool, Clipboard, X, FileText } from "react-feather";
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    service_date: string;
    odometer_reading: string;
    next_service_date: string;
    status: string;

    // Common
    total_cost?: string;
    invoice_reference?: string;
    invoice_document?: FileList | string; // Changed to allow string URL from DB
    description?: string;

    // Parts Section
    parts_vendor_name?: string;
    parts_vendor_details?: string;
    parts_replaced: { name: string; amount: string }[];

    // Labour Section
    garage_name?: string; // Acts as Labour Vendor Name
    labour_vendor_details?: string;
    service_type?: string;
    labour_services: { item_description: string; unit_price: string }[];
}

interface InspectionState {
    inspection_date: string;
    inspection_type: string;
    inspection_status: string;
    vendor_name: string;
    vendor_details: string;
    report_files?: string[];
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditMaintenance() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = location.state || {};
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [serviceTypes, setServiceTypes] = useState<any[]>([]);
    const [inspectionTypes, setInspectionTypes] = useState<any[]>([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [pendingMessage, setPendingMessage] = useState("");
    const { hasPermission } = useAuth();
    const { config } = useConfig();
    // Workflow State
    const [maintenanceType, setMaintenanceType] = useState<'parts' | 'labour' | 'both'>('parts');
    const [sameVendor, setSameVendor] = useState(false);

    // ── Inspection State ──────────────────────
    const [includeInspection, setIncludeInspection] = useState(false);
    const [inspection, setInspection] = useState<InspectionState>({
        inspection_date: "",
        inspection_type: "",
        inspection_status: "Pending",
        vendor_name: "",
        vendor_details: "",
        report_files: [],
    });
    const [inspectionFiles, setInspectionFiles] = useState<File[]>([]);
    const [inspectionErrors, setInspectionErrors] = useState<Record<string, string>>({});
    const inspectionFileRef = useRef<React.ElementRef<'input'>>(null);

    const updateInspection = (key: keyof InspectionState, value: string) => {
        setInspection(prev => ({ ...prev, [key]: value }));
        setInspectionErrors(prev => ({ ...prev, [key]: "" }));
    };


    const removeExistingFile = (idx: number) => setInspection(prev => ({ ...prev, report_files: (prev.report_files || []).filter((_, i) => i !== idx) }));

    const validateInspection = (): boolean => {
        const errs: Record<string, string> = {};
        if (!inspection.inspection_date) errs.inspection_date = "Inspection date is required";
        if (!inspection.inspection_type.trim()) errs.inspection_type = "Inspection type is required";
        if (!inspection.inspection_status) errs.inspection_status = "Status is required";
        if (!inspection.vendor_name.trim()) errs.vendor_name = "Vendor name is required";
        if (!inspection.vendor_details.trim()) errs.vendor_details = "Vendor details are required";
        // Files are required: either existing DB files OR newly selected files
        const hasExistingFiles = (inspection.report_files || []).length > 0;
        const hasNewFiles = inspectionFiles.length > 0;
        if (!hasExistingFiles && !hasNewFiles) errs.report_files = "At least one report file is required";
        setInspectionErrors(errs);
        return Object.keys(errs).length === 0;
    };
    // ─────────────────────────────────────────

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        register,
        reset,
        trigger
    } = useForm<FormValues>({
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            parts_replaced: [{ name: "", amount: "" }],
            labour_services: [{ item_description: "", unit_price: "" }]
        }
    });

    const partsArray = useFieldArray({
        control,
        name: "parts_replaced"
    });

    const labourArray = useFieldArray({
        control,
        name: "labour_services"
    });

    const selectedBranch = watch("branch_id");
    const selectedVehicle = watch("vehicle_id");
    const watchedStatus = watch("status");
    const watchedServiceDate = watch("service_date");
    const watchedNextServiceDate = watch("next_service_date");

    // Watch for calculations
    const watchedParts = watch("parts_replaced");
    const watchedLabour = watch("labour_services");

    // Sync Vendor Details
    const partsVendorName = watch("parts_vendor_name");
    const partsVendorDetails = watch("parts_vendor_details");

    useEffect(() => {
        if (maintenanceType === 'both' && sameVendor) {
            setValue("garage_name", partsVendorName, { shouldValidate: !!partsVendorName });
            setValue("labour_vendor_details", partsVendorDetails);
        }
    }, [sameVendor, partsVendorName, partsVendorDetails, maintenanceType, setValue, trigger]);

    // Calculate Totals — only include active sections based on maintenance type
    const partsTotal = (maintenanceType === 'parts' || maintenanceType === 'both')
        ? (watchedParts || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
        : 0;
    const labourTotal = (maintenanceType === 'labour' || maintenanceType === 'both')
        ? (watchedLabour || []).reduce((sum, item) => sum + (Number(item.unit_price) || 0), 0)
        : 0;
    const grandTotal = (partsTotal + labourTotal).toFixed(2);

    useEffect(() => {
        setValue("total_cost", grandTotal);
    }, [grandTotal, setValue]);

    // Check for pending maintenance (exclude current ID)
    useEffect(() => {
        if (!selectedVehicle || !id) {
            setSubmissionBlocked(false);
            setPendingMessage("");
            return;
        }

        const checkPending = async () => {
            try {
                const response: any = await apiPost("maintenance/check_pending", {
                    vehicle_id: selectedVehicle,
                    exclude_id: id
                });
                if (response && response.exists) {
                    setPendingMessage(response.message);
                    setSubmissionBlocked(true);
                } else {
                    setSubmissionBlocked(false);
                    setPendingMessage("");
                }
            } catch (error) {
                console.error("Error checking pending maintenance", error);
                setSubmissionBlocked(false);
                setPendingMessage("");
            }
        };

        checkPending();
    }, [selectedVehicle, id]);

    // Fetch Branches & Master Data
    useEffect(() => {
        const fetchBaseData = async () => {
            try {
                const branchRes: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchRes);

                // Fetch Service Types
                const serviceRes: any = await apiPost('master-entries/list', { type: 'service_type' });
                setServiceTypes(Array.isArray(serviceRes) ? serviceRes : []);

                // Fetch Inspection Types
                const inspectionRes: any = await apiPost('master-entries/list', { type: 'inspection_type' });
                setInspectionTypes(Array.isArray(inspectionRes) ? inspectionRes : []);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBaseData();
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) return;
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };
        fetchVehicles();
    }, [selectedBranch]);

    // Fetch Maintenance Data
    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            try {
                const data: any = await apiPost("maintenance/viewbyid", { id });

                // Determine Type
                const hasPartsItems = data.parts_replaced && data.parts_replaced.length > 0;
                // Labour services is new, so check garage_name too for legacy compatibility
                const hasLabourItems = data.labour_services && data.labour_services.length > 0;
                const hasLabourVendor = !!data.garage_name;
                const hasPartsVendor = !!data.parts_vendor_name;

                if ((hasPartsItems || hasPartsVendor) && (hasLabourItems || hasLabourVendor)) {
                    setMaintenanceType('both');
                } else if (hasLabourItems || hasLabourVendor) {
                    setMaintenanceType('labour');
                } else {
                    setMaintenanceType('parts');
                }

                setValue("branch_id", data.branch_id);
                try {
                    const vResponse: any = await apiPost("vehicles/list", { branch_id: data.branch_id });
                    let vList = [];
                    if (Array.isArray(vResponse)) {
                        vList = vResponse.filter((v: any) => v.status !== 'Sold');
                    } else if (vResponse.data) {
                        vList = Array.isArray(vResponse.data) ? vResponse.data.filter((v: any) => v.status !== 'Sold') : [];
                    }
                    setVehicles(vList);
                    setValue("vehicle_id", data.vehicle_id);
                } catch (e) {
                    console.error(e);
                }

                setValue("service_date", data.service_date);
                setValue("odometer_reading", data.odometer_reading);
                setValue("next_service_date", data.next_service_date);
                setValue("status", data.status);
                // total_cost handled by auto-calc mostly, but set initial
                // setValue("total_cost", data.total_cost);
                setValue("invoice_reference", data.invoice_reference);
                setValue("invoice_document", data.invoice_document);
                setValue("description", data.description);

                // Parts
                setValue("parts_vendor_name", data.parts_vendor_name);
                setValue("parts_vendor_details", data.parts_vendor_details);

                // Labour
                setValue("garage_name", data.garage_name);
                setValue("labour_vendor_details", data.labour_vendor_details);
                setValue("service_type", data.service_type);

                // Populate inspection data if present
                if (data.inspection_data) {
                    setIncludeInspection(true);
                    setInspection({
                        inspection_date: data.inspection_data.inspection_date || "",
                        inspection_type: data.inspection_data.inspection_type || "",
                        inspection_status: data.inspection_data.inspection_status || "Pending",
                        vendor_name: data.inspection_data.vendor_name || "",
                        vendor_details: data.inspection_data.vendor_details || "",
                        report_files: data.inspection_data.report_files || [],
                    });
                }

                // Items
                if (data.parts_replaced && Array.isArray(data.parts_replaced)) {
                    // Check if legacy string array or object array with helper
                    const formattedParts = data.parts_replaced.map((p: any) => {
                        if (typeof p === 'string') return { name: p, amount: "" };
                        return { name: p.name || "", amount: p.amount ? String(p.amount) : "" };
                    });

                    // Reset with data
                    setValue("parts_replaced", formattedParts.length > 0 ? formattedParts : [{ name: "", amount: "" }]);
                } else {
                    setValue("parts_replaced", [{ name: "", amount: "" }]);
                }

                if (data.labour_services && Array.isArray(data.labour_services)) {
                    const formattedLabour = data.labour_services.map((p: any) => {
                        return { item_description: p.item_description || "", unit_price: p.unit_price ? String(p.unit_price) : "" };
                    });
                    setValue("labour_services", formattedLabour.length > 0 ? formattedLabour : [{ item_description: "", unit_price: "" }]);
                } else {
                    setValue("labour_services", [{ item_description: "", unit_price: "" }]);
                }

                setInitialLoading(false);
                // Clear any stale validation state from setValue calls above
                trigger();

            } catch (error) {
                console.error("Error fetching maintenance details:", error);
                toast.error("Failed to load maintenance details");
            }
        };

        fetchData();
    }, [id, setValue]);


    const onInvalid = () => {
        if (includeInspection) {
            const isInspectionValid = validateInspection();
            if (!isInspectionValid) {
                // toast.error("Please fill in the required Inspection fields.");
            }
        }
    };

    const onSubmit = async (data: FormValues) => {
        if (submissionBlocked && data.status !== 'Completed') {
            toast.error(pendingMessage || "Cannot submit: A pending maintenance record already exists for this vehicle.");
            return;
        }
        // Validate inspection if enabled
        if (includeInspection && !validateInspection()) {
            // toast.error("Please fill in the required Inspection fields.");
            return;
        }
        setLoading(true);
        try {
            let invoiceDocName = "";
            if (data.invoice_document && data.invoice_document.length > 0 && typeof data.invoice_document !== 'string') {
                const file = data.invoice_document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                invoiceDocName = await uploadFile(file);
            } else if (typeof data.invoice_document === 'string') {
                invoiceDocName = data.invoice_document;
            }

            const formattedServiceDate = data.service_date ? formatDate(new Date(data.service_date)) : "";
            const formattedNextServiceDate = data.next_service_date ? formatDate(new Date(data.next_service_date)) : "";

            // Filter Empty Items
            const parts = (data.parts_replaced || []).filter(n => n.name && n.name.trim() !== "");
            const labour = (data.labour_services || []).filter(n => n.item_description && n.item_description.trim() !== "");

            // Prepare Payload Base
            const payload: any = {
                id: id,
                ...data,
                service_date: formattedServiceDate,
                next_service_date: formattedNextServiceDate,
                invoice_document: invoiceDocName,
                total_cost: grandTotal,
            };

            // Handle Workflow Logic
            if (maintenanceType === 'parts') {
                payload.labour_services = [];
                payload.garage_name = null;
                payload.labour_vendor_details = null;
                payload.service_type = data.service_type || "Parts Only"; // Preserve user's typed category
                payload.parts_replaced = parts;
            } else if (maintenanceType === 'labour') {
                payload.parts_replaced = [];
                payload.parts_vendor_name = null;
                payload.parts_vendor_details = null;
                payload.labour_services = labour;
            } else {
                // Hybrid
                payload.parts_replaced = parts;
                payload.labour_services = labour;
            }

            // Build inspection_data
            if (includeInspection) {
                const newFiles: string[] = [];
                for (const file of inspectionFiles) {
                    const fname = await (uploadFile as any)(file);
                    newFiles.push(fname);
                }
                payload.inspection_data = {
                    branch_id: data.branch_id,
                    vehicle_id: data.vehicle_id,
                    inspection_date: inspection.inspection_date,
                    inspection_type: inspection.inspection_type,
                    inspection_status: inspection.inspection_status,
                    vendor_name: inspection.vendor_name || null,
                    vendor_details: inspection.vendor_details || null,
                    report_files: [...(inspection.report_files || []), ...newFiles],
                };
            } else {
                payload.inspection_data = null;
            }

            await apiPost("maintenance/update", payload);
            toast.success("Maintenance record updated successfully", { autoClose: 1000 });
            setTimeout(() => {
                reset();
                navigate(`${process.env.PUBLIC_URL}/maintenance`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error updating maintenance");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Edit Maintenance" parent="Maintenance" title="Edit Maintenance" />
            {initialLoading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <div className="d-flex justify-content-center align-items-center mb-4 gap-4 flex-wrap">
                        <div className="btn-group">
                            <Button color={maintenanceType === 'parts' ? 'primary' : 'outline-primary'} onClick={() => setMaintenanceType('parts')} active={maintenanceType === 'parts'}>
                                <Disc size={16} className="me-2" />Parts Only
                            </Button>
                            <Button color={maintenanceType === 'labour' ? 'primary' : 'outline-primary'} onClick={() => setMaintenanceType('labour')} active={maintenanceType === 'labour'}>
                                <Tool size={16} className="me-2" />Labour Only
                            </Button>
                            <Button color={maintenanceType === 'both' ? 'primary' : 'outline-primary'} onClick={() => setMaintenanceType('both')} active={maintenanceType === 'both'}>
                                <span className="me-2">⚡</span>Hybrid (Both)
                            </Button>
                        </div>

                        {/* Inspection Toggle */}
                        <div
                            className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill border"
                            style={{
                                background: includeInspection ? 'linear-gradient(135deg,#e8f5e9,#f1f8e9)' : '#f8f9fa',
                                borderColor: includeInspection ? '#4caf50' : '#dee2e6',
                                cursor: 'pointer',
                                transition: 'all .25s',
                            }}
                            onClick={() => setIncludeInspection(v => !v)}
                        >
                            <Input
                                type="checkbox"
                                id="edit_inspection_toggle"
                                checked={includeInspection}
                                onChange={() => setIncludeInspection(v => !v)}
                                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#4caf50' }}
                                onClick={e => e.stopPropagation()}
                            />
                            <Clipboard size={18} color={includeInspection ? '#2e7d32' : '#6c757d'} />
                            <Label
                                htmlFor="edit_inspection_toggle"
                                className="mb-0 fw-semibold"
                                style={{ cursor: 'pointer', color: includeInspection ? '#2e7d32' : '#495057', userSelect: 'none' }}
                            >
                                Include Inspection Report
                            </Label>
                            {includeInspection && <Badge color="success" pill>Active</Badge>}
                        </div>
                    </div>

                    <Card>

                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label>Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="branch_id"
                                            control={control}
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                        <option value="">Select Branch</option>
                                                        {branches.map((b: any) => (
                                                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Vehicle <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="vehicle_id"
                                            control={control}
                                            rules={{ required: "Vehicle is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.vehicle_id} disabled={!selectedBranch}>
                                                        <option value="">Select Vehicle</option>
                                                        {vehicles.map((v: any) => (
                                                            <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Service Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="service_date"
                                            control={control}
                                            rules={{ required: "Service Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.service_date ? "is-invalid" : ""}`}
                                                        wrapperClassName="w-100"
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedNextServiceDate && d && new Date(d) > new Date(watchedNextServiceDate)) {
                                                                toast.error("Service date cannot be after Next Service date");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <FormFeedback className="d-block">{errors.service_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Odometer Reading (km) <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="odometer_reading"
                                            control={control}
                                            rules={{ required: "Odometer is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="number" {...field} placeholder="12000" invalid={!!errors.odometer_reading} autoComplete="off" />
                                                    <FormFeedback>{errors.odometer_reading?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Category / Service Type <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="service_type"
                                            control={control}
                                            rules={{ required: "Category / Service Type is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.service_type}>
                                                        <option value="">Select Service Type</option>
                                                        {serviceTypes.map((t: any) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                                    </Input>
                                                    <FormFeedback>{errors.service_type?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Status <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="status"
                                            control={control}
                                            rules={{ required: "Status is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.status}>
                                                        <option value="">Select Status</option>
                                                        <option value="Pending">Pending</option>
                                                        <option value="In Progress">In Progress</option>
                                                        <option value="Completed">Completed</option>
                                                    </Input>
                                                    <FormFeedback>{errors.status?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>

                                {/* ═══════════════════════════════════════════
                                      INSPECTION REPORT SECTION (optional)
                                  ═══════════════════════════════════════════ */}
                                {includeInspection && (
                                    <Card className="mb-4 shadow-sm mt-4" style={{ border: '2px solid #4caf50', borderRadius: 12 }}>
                                        <CardBody>
                                            <div className="d-flex align-items-center gap-2 mb-3">
                                                <Clipboard size={22} color="#2e7d32" />
                                                <H5 className="mb-0" style={{ color: '#2e7d32' }}>Inspection Details</H5>
                                            </div>
                                            {(watch('branch_id') || watch('vehicle_id')) && (
                                                <div className="alert alert-success py-2 mb-3 small text-dark">
                                                    <strong>Linked to:</strong>
                                                    {watch('branch_id') && branches.find((b: any) => String(b.id) === String(watch('branch_id'))) && (
                                                        <span className="ms-2">Branch — <strong>{branches.find((b: any) => String(b.id) === String(watch('branch_id')))?.branch_name}</strong></span>
                                                    )}
                                                    {watch('vehicle_id') && vehicles.find((v: any) => String(v.id) === String(watch('vehicle_id'))) && (
                                                        <span className="ms-2">· Vehicle — <strong>{vehicles.find((v: any) => String(v.id) === String(watch('vehicle_id')))?.registration_number}</strong></span>
                                                    )}
                                                </div>
                                            )}
                                            <Row className="g-3">
                                                <Col md={4}>
                                                    <Label>Inspection Date <span className="text-danger">*</span></Label>
                                                    <DatePicker className={`form-control ${inspectionErrors.inspection_date ? "is-invalid" : ""}`} wrapperClassName="w-100"
                                                        selected={inspection.inspection_date ? new Date(inspection.inspection_date) : null}
                                                        onChange={(date) => updateInspection("inspection_date", date ? formatDate(date) : "")}
                                                        placeholderText="YYYY-MM-DD" showMonthDropdown showYearDropdown dropdownMode="select" />
                                                    {inspectionErrors.inspection_date && <div className="invalid-feedback d-block">{inspectionErrors.inspection_date}</div>}
                                                </Col>
                                                <Col md={4}>
                                                    <Label>Inspection Type <span className="text-danger">*</span></Label>
                                                    <Input type="select" value={inspection.inspection_type} onChange={e => updateInspection("inspection_type", e.target.value)} invalid={!!inspectionErrors.inspection_type}>
                                                        <option value="">Select Inspection Type</option>
                                                        {inspectionTypes.map((t: any) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                                    </Input>
                                                    {inspectionErrors.inspection_type && <FormFeedback className="d-block">{inspectionErrors.inspection_type}</FormFeedback>}
                                                </Col>
                                                <Col md={4}>
                                                    <Label>Status <span className="text-danger">*</span></Label>
                                                    <Input type="select" value={inspection.inspection_status} onChange={e => updateInspection("inspection_status", e.target.value)} invalid={!!inspectionErrors.inspection_status}>
                                                        <option value="Pending">Pending</option>
                                                        <option value="In Progress">In Progress</option>
                                                        <option value="Passed">Passed</option>
                                                        <option value="Failed">Failed</option>
                                                    </Input>
                                                    {inspectionErrors.inspection_status && <FormFeedback className="d-block">{inspectionErrors.inspection_status}</FormFeedback>}
                                                </Col>
                                                <Col md={6}>
                                                    <Label>Inspection Vendor Name <span className="text-danger">*</span></Label>
                                                    <Input value={inspection.vendor_name} onChange={e => updateInspection("vendor_name", e.target.value)} placeholder="Inspector / Agency" invalid={!!inspectionErrors.vendor_name} autoComplete="off" />
                                                    {inspectionErrors.vendor_name && <FormFeedback className="d-block">{inspectionErrors.vendor_name}</FormFeedback>}
                                                </Col>
                                                <Col md={6}>
                                                    <Label>Vendor Details <span className="text-danger">*</span></Label>
                                                    <Input type="textarea" rows={1} value={inspection.vendor_details} onChange={e => updateInspection("vendor_details", e.target.value)} placeholder="Address / Contact" invalid={!!inspectionErrors.vendor_details} autoComplete="off" />
                                                    {inspectionErrors.vendor_details && <FormFeedback className="d-block">{inspectionErrors.vendor_details}</FormFeedback>}
                                                </Col>

                                                <Col md={12}>
                                                    <Label>Inspection Report Files <span className="text-danger">*</span> <span className="text-muted small">(Photos / PDFs)</span></Label>
                                                    <Input type="file" multiple accept="image/*,.pdf"
                                                        invalid={!!inspectionErrors.report_files}
                                                        onChange={(e) => {
                                                            if (e.target.files) {
                                                                setInspectionFiles(Array.from(e.target.files));
                                                                setInspectionErrors(prev => ({ ...prev, report_files: '' }));
                                                            }
                                                        }} />
                                                    {inspectionErrors.report_files && <FormFeedback className="d-block">{inspectionErrors.report_files}</FormFeedback>}

                                                    {/* Existing Files - links similar to accident screen */}
                                                    {(inspection.report_files || []).length > 0 && (
                                                        <div className="d-flex flex-wrap gap-3 mt-2">
                                                            {(inspection.report_files || []).map((file, idx) => (
                                                                <div key={idx} className="d-flex align-items-center gap-1">
                                                                    <a href={`${mediaUrl}${file}`} target="_blank" rel="noreferrer" className="text-success small">
                                                                        <FileText size={14} className="me-1" /> View Report {idx + 1}
                                                                    </a>
                                                                    <X size={14} className="text-danger cursor-pointer" onClick={() => removeExistingFile(idx)} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* New Files Preview */}
                                                    {inspectionFiles.length > 0 && (
                                                        <div className="text-info mt-1 small">
                                                            {inspectionFiles.length} new file(s) selected for upload.
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                )}

                                <Row className="mt-4">
                                    {/* PARTS SECTION */}
                                    {(maintenanceType === 'parts' || maintenanceType === 'both') && (
                                        <Col md={maintenanceType === 'both' ? 6 : 12}>
                                            <Card className="border-top-primary shadow-sm">
                                                <CardBody>
                                                    <div className="d-flex justify-content-between mb-3 align-items-center">
                                                        <H5 className="text-primary mb-0"><Disc className="me-2" size={20} />Parts Details</H5>
                                                        <Badge color="primary" pill className="fs-6">{partsTotal.toFixed(2)}</Badge>
                                                    </div>
                                                    <Row className="g-2 mb-2">
                                                        <Col md={12}>
                                                            <Label>Vendor Name <span className="text-danger">*</span></Label>
                                                            <Controller
                                                                control={control}
                                                                name="parts_vendor_name"
                                                                rules={{ required: "Parts Vendor is required" }}
                                                                render={({ field }) => (
                                                                    <>
                                                                        <Input
                                                                            {...field}
                                                                            placeholder="Supplier Name"
                                                                            invalid={!!errors.parts_vendor_name} autoComplete="off"
                                                                        />
                                                                        <FormFeedback>{errors.parts_vendor_name?.message}</FormFeedback>
                                                                    </>
                                                                )}
                                                            />
                                                        </Col>
                                                        <Col md={12}>
                                                            <Label>Vendor Details</Label>
                                                            <Controller
                                                                control={control}
                                                                name="parts_vendor_details"
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type="textarea"
                                                                        rows={2}
                                                                        {...field}
                                                                        placeholder="Address / Contact"
                                                                        autoComplete="off"
                                                                    />
                                                                )}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <Label className="fw-bold">Parts List <span className="text-danger">*</span></Label>
                                                    {partsArray.fields.map((field, index) => (
                                                        <Row key={field.id} className="mb-2 g-2 align-items-center">
                                                            <Col xs={7}>
                                                                <Controller
                                                                    control={control}
                                                                    name={`parts_replaced.${index}.name`}
                                                                    rules={{ required: "Part name is required" }}
                                                                    render={({ field }) => (
                                                                        <>
                                                                            <Input
                                                                                {...field}
                                                                                placeholder="Part Name"
                                                                                invalid={!!errors.parts_replaced?.[index]?.name}
                                                                                autoComplete="off"
                                                                            />
                                                                            {errors.parts_replaced?.[index]?.name && (
                                                                                <FormFeedback className="d-block">
                                                                                    {errors.parts_replaced[index]?.name?.message}
                                                                                </FormFeedback>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                />
                                                            </Col>
                                                            <Col xs={4}>
                                                                <Controller
                                                                    control={control}
                                                                    name={`parts_replaced.${index}.amount`}
                                                                    rules={{
                                                                        required: "Price is required",
                                                                        min: { value: 0.01, message: "Price should be greater than 0" }
                                                                    }}
                                                                    render={({ field }) => (
                                                                        <>
                                                                            <Input
                                                                                type="number"
                                                                                {...field}
                                                                                placeholder={`Price (${config.currency})`}
                                                                                invalid={!!errors.parts_replaced?.[index]?.amount}
                                                                                autoComplete="off"
                                                                            />
                                                                            {errors.parts_replaced?.[index]?.amount && (
                                                                                <FormFeedback className="d-block">
                                                                                    {errors.parts_replaced[index]?.amount?.message}
                                                                                </FormFeedback>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                />
                                                            </Col>
                                                            <Col xs={1}>
                                                                {partsArray.fields.length > 1 && (
                                                                    <Trash2 size={16} className="text-danger cursor-pointer" onClick={() => partsArray.remove(index)} />
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    ))}
                                                    <Button size="sm" color="light" className="w-100 text-primary" onClick={() => partsArray.append({ name: "", amount: "" })}>
                                                        <Plus size={14} className="me-1" /> Add Part
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    )}

                                    {/* LABOUR SECTION */}
                                    {(maintenanceType === 'labour' || maintenanceType === 'both') && (
                                        <Col md={maintenanceType === 'both' ? 6 : 12}>
                                            <Card className="border-top-warning shadow-sm">
                                                <CardBody>
                                                    <div className="d-flex justify-content-between mb-3 align-items-center">
                                                        <H5 className="text-warning mb-0"><Tool className="me-2" size={20} />Labour Details</H5>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {maintenanceType === 'both' && (
                                                                <div className="form-check form-switch mb-0">
                                                                    <Input type="checkbox" className="form-check-input" id="sameVendorSwitch" checked={sameVendor} onChange={(e) => setSameVendor(e.target.checked)} style={{ transform: 'scale(1.2)', borderColor: sameVendor ? '#198754' : '', backgroundColor: sameVendor ? '#198754' : '' }} />
                                                                    <Label className="ms-2 text-success fw-bold" htmlFor="sameVendorSwitch">Same as Parts</Label>
                                                                </div>
                                                            )}
                                                            <Badge color="warning" pill className="fs-6 text-dark">{labourTotal.toFixed(2)}</Badge>
                                                        </div>
                                                    </div>

                                                    <Row className="g-2 mb-2">
                                                        <Col md={12}>
                                                            <Label>Garage / Service Center <span className="text-danger">*</span></Label>
                                                            <Controller
                                                                control={control}
                                                                name="garage_name"
                                                                rules={{ required: "Garage Name is required" }}
                                                                render={({ field }) => (
                                                                    <>
                                                                        <Input
                                                                            {...field}
                                                                            placeholder="Garage Name"
                                                                            invalid={!!errors.garage_name}
                                                                            disabled={sameVendor && maintenanceType === 'both'} autoComplete="off"
                                                                        />
                                                                        <FormFeedback>{errors.garage_name?.message}</FormFeedback>
                                                                    </>
                                                                )}
                                                            />
                                                        </Col>
                                                        <Col md={12}>
                                                            <Label>Vendor Details</Label>
                                                            <Controller
                                                                control={control}
                                                                name="labour_vendor_details"
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type="textarea"
                                                                        rows={2}
                                                                        {...field}
                                                                        placeholder="Address / Contact"
                                                                        disabled={sameVendor && maintenanceType === 'both'} autoComplete="off"
                                                                    />
                                                                )}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <Label className="fw-bold ">Services List <span className="text-danger">*</span></Label>
                                                    {labourArray.fields.map((field, index) => (
                                                        <Row key={field.id} className="mb-2 g-2 align-items-center">
                                                            <Col xs={7}>
                                                                <Controller
                                                                    control={control}
                                                                    name={`labour_services.${index}.item_description`}
                                                                    rules={{ required: "Service description is required" }}
                                                                    render={({ field }) => (
                                                                        <>
                                                                            <Input
                                                                                {...field}
                                                                                placeholder="Service Desc."
                                                                                invalid={!!errors.labour_services?.[index]?.item_description}
                                                                                autoComplete="off"
                                                                            />
                                                                            {errors.labour_services?.[index]?.item_description && (
                                                                                <FormFeedback className="d-block">
                                                                                    {errors.labour_services[index]?.item_description?.message}
                                                                                </FormFeedback>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                />
                                                            </Col>
                                                            <Col xs={4}>
                                                                <Controller
                                                                    control={control}
                                                                    name={`labour_services.${index}.unit_price`}
                                                                    rules={{
                                                                        required: "Cost is required",
                                                                        min: { value: 0.01, message: "Cost should be greater than 0" }
                                                                    }}
                                                                    render={({ field }) => (
                                                                        <>
                                                                            <Input
                                                                                type="number"
                                                                                {...field}
                                                                                placeholder={`Cost (${config.currency})`}
                                                                                invalid={!!errors.labour_services?.[index]?.unit_price}
                                                                                autoComplete="off"
                                                                            />
                                                                            {errors.labour_services?.[index]?.unit_price && (
                                                                                <FormFeedback className="d-block">
                                                                                    {errors.labour_services[index]?.unit_price?.message}
                                                                                </FormFeedback>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                />
                                                            </Col>
                                                            <Col xs={1}>
                                                                {labourArray.fields.length > 1 && (
                                                                    <Trash2 size={16} className="text-danger cursor-pointer" onClick={() => labourArray.remove(index)} />
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    ))}
                                                    <Button size="sm" color="light" className="w-100 text-warning" onClick={() => labourArray.append({ item_description: "", unit_price: "" })}>
                                                        <Plus size={14} className="me-1" /> Add Service
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    )}
                                </Row>

                                <Row className="mt-4">
                                    <Col md={4}>
                                        <Label>Total Cost (Auto-Calc) {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                        <Input
                                            value={grandTotal}
                                            readOnly
                                            className="bg-white fw-bold"
                                        />
                                        <input type="hidden" {...register("total_cost")} />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Invoice Reference {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                        <Controller
                                            name="invoice_reference"
                                            control={control}
                                            rules={{
                                                required: watchedStatus === 'Completed' ? "Invoice Reference is required" : false
                                            }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="INV-001" invalid={!!errors.invoice_reference} autoComplete="off" />
                                                    <FormFeedback>{errors.invoice_reference?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Upload Invoice {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                        <Controller
                                            name="invoice_document"
                                            control={control}
                                            rules={{
                                                required: watchedStatus === 'Completed' && !watch('invoice_document') ? "Invoice Document is required" : false
                                            }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        accept="image/*,.pdf"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files.length > 0) {
                                                                const file = e.target.files[0];
                                                                if (file.size > 2 * 1024 * 1024) {
                                                                    toast.error("File size must be less than 2MB");
                                                                    return;
                                                                }
                                                                onChange(e.target.files);
                                                            } else {
                                                                onChange(null);
                                                            }
                                                        }}
                                                        invalid={!!errors.invoice_document}
                                                    />
                                                    <FormFeedback>{errors.invoice_document?.message}</FormFeedback>

                                                    {typeof value === 'string' && value && (
                                                        <div className="mt-2">
                                                            <a href={`${mediaUrl}${value}`} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                                View Current Invoice
                                                            </a>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6} style={{ marginTop: "15px" }}>
                                        <Label>Next Service Due {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                        <Controller
                                            name="next_service_date"
                                            control={control}
                                            rules={{
                                                required: watchedStatus === 'Completed' ? "Next Service Date (Due) is required" : false
                                            }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.next_service_date ? "is-invalid" : ""}`}
                                                        wrapperClassName="w-100"
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => {
                                                            const d = date ? formatDate(date) : "";
                                                            if (watchedServiceDate && d && new Date(d) < new Date(watchedServiceDate)) {
                                                                toast.error("Next Service date cannot be before Service date");
                                                                return;
                                                            }
                                                            field.onChange(d);
                                                        }}
                                                        placeholderText="YYYY-MM-DD"
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                    />
                                                    <FormFeedback className="d-block">{errors.next_service_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={6} style={{ marginTop: "15px" }}>
                                        <Label>Description / Notes {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                        <Controller
                                            name="description"
                                            control={control}
                                            rules={{
                                                required: watchedStatus === 'Completed' ? "Description is required" : false
                                            }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="textarea" {...field} placeholder="Add notes..." invalid={!!errors.description} autoComplete="off" />
                                                    <FormFeedback>{errors.description?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                </Row>

                                <div className="mt-4">
                                    <Btn color="primary" disabled={loading} type="submit">
                                        {loading ? <>Updating <Spinner size="sm" /></> : "Update"}
                                    </Btn>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Container>
            )}
            <ToastContainer />
        </div>
    );
}
